import React, { useContext, useRef } from 'react';
import { Button, Upload, Tooltip } from 'antd';
import { GoogleOutlined, LoadingOutlined, DeleteOutlined, UploadOutlined, EyeOutlined, WarningOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import './index.scss';
import SettingContext from '../../contexts/settingProvider';

const { Dragger } = Upload;

const SingleUpload = ({
  file,
  isUploading,
  onFileChange,
  onGoogleDriveClick,
  onDeleteFile,
  acceptTypes,
  disabled,
  googleDriveEnabled = true
}) => {
  const { dark } = useContext(SettingContext);
  const { t } = useTranslation();
  const uploadRef = useRef(null);

  const draggerProps = {
    name: 'file',
    multiple: false,
    accept: acceptTypes,
    disabled: disabled || isUploading,
    beforeUpload: () => false,
    onChange: onFileChange,
    showUploadList: false,
    fileList: file ? [file] : [],
  };

  const handleSelectFileClick = () => {
    if (uploadRef.current && uploadRef.current.upload && uploadRef.current.upload.uploader) {
      const uploader = uploadRef.current.upload.uploader;
      if (typeof uploader.onClick === 'function') {
        // Try to find the actual input element, often stored on the uploader instance
        const inputEl = uploader.fileInput || uploader.inputRef?.current || uploader.input;
        try {
          // Pass a mock event object with the input element as target
          uploader.onClick({ target: inputEl || {} }); 
        } catch (error) {
          console.error('Error calling Antd uploader.onClick:', error, uploader);
          // If the above fails, it might be that onClick doesn't need an event or a different one.
          // As a last resort, try finding the input and clicking it directly if accessible.
          if (inputEl && typeof inputEl.click === 'function') {
            inputEl.click();
          } else {
            // Final fallback: try to find it in the Dragger's DOM if ref is the Dragger itself
            const draggerInputElement = uploadRef.current.querySelector('input[type="file"]');
            draggerInputElement?.click();
          }
        }
      } else {
        console.error('Antd uploader.onClick is not a function', uploader);
      }
    } else {
      console.error('Could not trigger Antd Upload click programmatically (ref structure issue)', uploadRef.current);
    }
  };

  return (
    <div className={`upload-section single-upload-section ${dark ? 'dark' : ''}`}>
      <div className="upload-area-single">
        <Dragger {...draggerProps} ref={uploadRef} className="custom-dragger-single">
          {isUploading && !file && (
            <div className="dragger-content loading-indicator-dragger">
              <p className="ant-upload-drag-icon"><LoadingOutlined /></p>
              <p className="ant-upload-text">{t('chatInput.multiUpload.uploading')}</p>
            </div>
          )}
          {!isUploading && file && (
            <div className="dragger-content file-item-dragger">
              <div className="file-name-and-status-dragger">
                <p className="ant-upload-text file-name-dragger">{file.source_name || file.name}</p>
                {file.hasPermission === false && (
                  <Tooltip title={t('chatInput.multiUpload.permissionWarning')}>
                    <WarningOutlined style={{ color: 'orange', marginLeft: '8px' }} />
                  </Tooltip>
                )}
              </div>
              <div className="file-actions-dragger">
                {file.source_source === 'drive' && (file.url || file.source_url) && (
                  <Button
                    type="link"
                    icon={<EyeOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(file.url || file.source_url, '_blank', 'noopener,noreferrer');
                    }}
                    className="view-button-dragger"
                    disabled={isUploading}
                  >
                    {t('chatInput.multiUpload.viewFile')}
                  </Button>
                )}
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    onDeleteFile(); 
                  }}
                  className="delete-button-dragger"
                  disabled={isUploading}
                >
                  {t('chatInput.multiUpload.removeFile')}
                </Button>
              </div>
            </div>
          )}
          {!isUploading && !file && (
            <div className="dragger-content empty-dragger">
              <p className="ant-upload-text">{t('chatInput.multiUpload.dropzoneText')}</p>
            </div>
          )}
        </Dragger>
        <div className="side-buttons-single">
          <Button 
            icon={<UploadOutlined />} 
            onClick={handleSelectFileClick} 
            disabled={disabled || isUploading}
            className="select-file-button-single"
          >
            {t('chatInput.multiUpload.selectFile')}
          </Button>
          {googleDriveEnabled && (
            <Button 
              icon={<GoogleOutlined />}
              onClick={onGoogleDriveClick}
              disabled={disabled || isUploading}
              className="google-drive-button-single"
            >
              {t('chatInput.multiUpload.googleDrive')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SingleUpload;